/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Immutable Credit Ledger & Financial Audit View (Task 10.6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Wallet,
  ShieldCheck,
  Download,
  Lock,
  Database,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import { useCreditLedgerStore } from '../stores/useCreditLedgerStore';
import { LedgerSummaryCards } from '../components/billing/LedgerSummaryCards';
import { CreditCategoryDistribution } from '../components/billing/CreditCategoryDistribution';
import { CreditLedgerTable } from '../components/billing/CreditLedgerTable';
import { TransactionDetailModal } from '../components/billing/TransactionDetailModal';

export const CreditLedgerView: React.FC = () => {
  const { t } = useTranslation();
  const { exportLedgerAsCsv, getLedgerStats, getFilteredEntries } = useCreditLedgerStore();
  const [copiedNotification, setCopiedNotification] = useState<string | null>(null);

  const stats = getLedgerStats();
  const filteredCount = getFilteredEntries().length;

  const handleExportCsv = () => {
    const csvContent = exportLedgerAsCsv();
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `sophia-credit-ledger-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    setCopiedNotification(t('ledger.exportSuccess'));
    setTimeout(() => setCopiedNotification(null), 3000);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Studio Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center justify-center">
              <Wallet className="size-5" />
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground">
              {t('ledger.title')}
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              D1 Ledger
            </span>
          </div>
          <p className="text-xs text-foreground-muted max-w-2xl">
            {t('ledger.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleExportCsv}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-xs font-medium shadow-xs transition-colors"
          >
            <Download className="size-3.5 text-foreground-muted" />
            <span>{t('ledger.exportCsv')}</span>
          </button>

          <div className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs font-semibold">
            <Sparkles className="size-3.5" />
            <span>{stats.currentBalance.toLocaleString()} {t('ledger.stats.credits')}</span>
          </div>
        </div>
      </div>

      {/* Export Notification Toast */}
      {copiedNotification && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium animate-in fade-in slide-in-from-top-2 duration-200">
          <CheckCircle2 className="size-4 flex-shrink-0 text-emerald-500" />
          <span>{copiedNotification}</span>
        </div>
      )}

      {/* 4 Financial KPI Summary Cards */}
      <LedgerSummaryCards />

      {/* Credit Consumption Breakdown by Module / Category */}
      <CreditCategoryDistribution />

      {/* Cryptographic D1 Immutable Guarantee Callout */}
      <div className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-r from-primary/5 via-surface to-accent/5 border border-primary/20 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
              <ShieldCheck className="size-4" />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground">
                  {t('ledger.immutableGuarantee.title')}
                </span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-primary/10 text-primary border border-primary/20">
                  <Lock className="size-2.5" />
                  SHA-256 Chained
                </span>
              </div>
              <p className="text-[11px] text-foreground-muted max-w-2xl leading-relaxed">
                {t('ledger.immutableGuarantee.description')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[11px] font-mono text-foreground-muted self-end sm:self-center bg-surface/80 px-2.5 py-1.5 rounded-lg border border-surface-border">
            <Database className="size-3 text-primary flex-shrink-0" />
            <span>@sofia/db: credit_ledger_entries ({filteredCount} records)</span>
          </div>
        </div>
      </div>

      {/* Filterable Data Table */}
      <CreditLedgerTable />

      {/* Transaction Detail Audit Modal */}
      <TransactionDetailModal />
    </div>
  );
};
